// One traveller's diary entry for one destination: their notes and their
// photos, plus the social controls.
//
// This is step 3 of the map's core interaction: tap a destination pin, pick a
// traveller, read their diary for that place. Their full timeline is one tap
// away from here.
import { useEffect, useState } from "react";
import { Icon, IconArrowLeft, IconBook, IconSend } from "@traveldiary/ui";
import { TravellerAvatar } from "./TravellerAvatar";
import { apiClient } from "../lib/api";
import { formatDateShort, formatRelative, formatTime } from "../lib/format";
import { FeedActions } from "./FeedActions";
import type { AuthoredMoment, Comment, PublicUser } from "../lib/types";
import { useApp } from "../state/app";

const MAX_COMMENT = 1000;

export interface MomentDetailProps {
  entry: AuthoredMoment;
  onBack?(): void;
  onOpenTimeline?(author: PublicUser): void;
}

export function MomentDetail({ entry, onBack, onOpenTimeline }: MomentDetailProps) {
  const { t, locale, exploreMoments } = useApp();
  // The store holds the live counters, so a like made here and a like made in
  // the feed can never drift apart.
  const current = exploreMoments.find((item) => item.id === entry.id) ?? entry;
  const author = current.author;

  return (
    <div className="entry-detail">
      {onBack ? (
        <button type="button" className="btn btn-quiet" onClick={onBack}>
          <Icon size={18}>
            <IconArrowLeft />
          </Icon>
          {t("map.backToPlace")}
        </button>
      ) : null}

      {author ? (
        <button
          type="button"
          className="entry-author"
          onClick={() => onOpenTimeline?.(author)}
          disabled={!onOpenTimeline}
        >
          <TravellerAvatar user={author} size="md" />
          <span className="who-text">
            <span className="who-name">{author.displayName}</span>
            <span className="who-meta">
              {current.tripTitle ? `${current.tripTitle} ` : ""}
              {formatDateShort(current.capturedAt, locale)}
            </span>
          </span>
          {onOpenTimeline ? (
            <span className="entry-author-cta">
              <Icon size={16} color="var(--color-text-muted)">
                <IconBook />
              </Icon>
              {t("map.openTimeline")}
            </span>
          ) : null}
        </button>
      ) : null}

      {current.media.length > 0 ? (
        <div className="entry-photos">
          {current.media.map((media) =>
            media.mime.startsWith("video/") ? (
              <video
                key={media.id}
                className="entry-photo"
                src={apiClient.mediaUrl(media.storageKey)}
                controls
              />
            ) : (
              <img
                key={media.id}
                className="entry-photo"
                src={apiClient.mediaUrl(media.storageKey)}
                alt=""
                loading="lazy"
              />
            ),
          )}
        </div>
      ) : null}

      <p className="entry-notes">{current.caption || t("diary.noCaption")}</p>
      <p className="entry-when">{formatTime(current.capturedAt, locale)}</p>

      <FeedActions entry={current} />
      <Comments moment={current} />
    </div>
  );
}

/**
 * Comments are PLAIN TEXT. They are rendered as text nodes and never as HTML:
 * this is the one surface on a public feed where user supplied content is
 * displayed to strangers.
 */
function Comments({ moment }: { moment: AuthoredMoment }) {
  const { t, locale, bumpCommentCount } = useApp();
  const [items, setItems] = useState<Comment[] | null>(null);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setItems(null);
    setUnavailable(false);
    void apiClient
      .listComments(moment.id)
      .then((list) => {
        if (!cancelled) setItems(list);
      })
      .catch(() => {
        if (!cancelled) setUnavailable(true);
      });
    return () => {
      cancelled = true;
    };
  }, [moment.id]);

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    const trimmed = body.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    try {
      const created = await apiClient.addComment(moment.id, trimmed.slice(0, MAX_COMMENT));
      setItems((current) => [...(current ?? []), created]);
      bumpCommentCount(moment.id, 1);
      setBody("");
    } catch {
      setUnavailable(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="comments">
      {unavailable ? (
        <p className="field-note" role="status">
          {t("feed.commentsUnavailable")}
        </p>
      ) : null}

      <ul className="comment-list">
        {(items ?? []).map((comment) => (
          <li className="comment" key={comment.id}>
            <TravellerAvatar user={comment.author} size="xs" />
            <span className="comment-text">
              <span className="comment-name">{comment.author?.displayName ?? ""}</span>
              {/* Text node on purpose: never dangerouslySetInnerHTML here. */}
              <span className="comment-body">{comment.body}</span>
              <span className="comment-when">{formatRelative(comment.createdAt, locale)}</span>
            </span>
          </li>
        ))}
      </ul>

      <form className="comment-form" onSubmit={submit}>
        <input
          className="input"
          name="comment"
          value={body}
          maxLength={MAX_COMMENT}
          placeholder={t("feed.commentPlaceholder")}
          onChange={(event) => setBody(event.target.value)}
          disabled={unavailable}
        />
        <button
          type="submit"
          className="btn btn-primary"
          disabled={busy || unavailable || body.trim().length === 0}
          aria-label={t("feed.send")}
        >
          <Icon size={18}>
            <IconSend />
          </Icon>
        </button>
      </form>
    </div>
  );
}
